import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, Eye, Share2, X, Download } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchPosts } from '../services/newsApi';

const UPLOADS_BASE = 'https://api.fsgu.co.za/uploads/posts/';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const { isDarkMode } = useTheme();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxAlt, setLightboxAlt] = useState('');

  useEffect(() => {
    loadPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const posts = await fetchPosts({ status: 'published' });

      const foundPost = posts.find(p => p.slug === slug);
      if (!foundPost) throw new Error('Post not found');

      setPost(foundPost);

      const related = posts
        .filter(p => p.category_id === foundPost.category_id && p.id !== foundPost.id)
        .slice(0, 3);
      setRelatedPosts(related);

      incrementViewCount(foundPost.id);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (postId) => {
    try {
      await fetch(`https://api.fsgu.co.za/increment-view.php?id=${postId}`);
    } catch (error) {
      console.error('Error incrementing view:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (!post) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const openLightbox = useCallback((src, alt = '') => {
    if (!src) return;
    setLightboxSrc(src);
    setLightboxAlt(alt || '');
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    // keep src/alt around briefly; not required to clear, but fine either way
  }, []);

  // Close on ESC
  useEffect(() => {
    if (!lightboxOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen, closeLightbox]);

  // Download helper (forces download even if browser would open)
  const downloadImage = async (url) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      // best-effort filename
      const parts = url.split('/');
      const filename = decodeURIComponent(parts[parts.length - 1] || 'image');

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // fallback: open in new tab (user can save)
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Fix old/bad image src patterns at render-time
  const normalizePostHtml = (html) => {
    if (!html) return '';

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      doc.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (!src) return;

        // already absolute/data
        if (/^https?:\/\//i.test(src) || /^data:/i.test(src)) {
          return;
        }

        const cleaned = src.replace(/^public_html\/?/i, '').replace(/^\/+/, '');

        const match = cleaned.match(/(?:^|\/)(?:api\/)?uploads\/posts\/(.+)$/i);
        if (match && match[1]) {
          img.setAttribute('src', UPLOADS_BASE + match[1]);
          return;
        }

        if (!src.includes('/')) {
          img.setAttribute('src', UPLOADS_BASE + src);
          return;
        }

        const match2 = cleaned.match(/(?:^|\/)posts\/(.+)$/i);
        if (match2 && match2[1]) {
          img.setAttribute('src', UPLOADS_BASE + match2[1]);
        }
      });

      return doc.body.innerHTML;
    } catch (e) {
      return html;
    }
  };

  const safeContentHtml = useMemo(() => {
    if (!post?.content) return '';
    return normalizePostHtml(post.content);
  }, [post?.content]);

  // Attach click handlers to images inside the rendered HTML
  useEffect(() => {
    // Wait until the content is in the DOM
    const container = document.getElementById('news-post-content');
    if (!container) return;

    const imgs = Array.from(container.querySelectorAll('img'));
    if (imgs.length === 0) return;

    const handlers = [];

    imgs.forEach((img) => {
      // Make it feel clickable
      img.style.cursor = 'zoom-in';

      const onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const src = img.getAttribute('src') || '';
        const alt = img.getAttribute('alt') || '';
        openLightbox(src, alt);
      };

      img.addEventListener('click', onClick);
      handlers.push(() => img.removeEventListener('click', onClick));
    });

    return () => {
      handlers.forEach((off) => off());
    };
  }, [safeContentHtml, openLightbox]);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-white'}`}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500 mb-4"></div>
            <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
              Loading article...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-white'}`}>
        <div className="section py-20">
          <div className="container-custom text-center">
            <h1 className={`text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Article Not Found
            </h1>
            <p className={`mb-8 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/news" className="btn btn-primary">
              Back to News
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-white'}`}>
      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/80' : 'bg-black/70'}`} />

          {/* Modal */}
          <div
            className={`relative w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl border ${
              isDarkMode ? 'bg-fsgu-gray-900 border-fsgu-gray-700' : 'bg-white border-fsgu-silver-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${
              isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
            }`}>
              <div className={`text-sm truncate pr-4 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                {lightboxAlt || 'Image preview'}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadImage(lightboxSrc)}
                  className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 hover:bg-fsgu-gray-750 text-fsgu-silver-100'
                      : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-900'
                  }`}
                  title="Download image"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>

                <button
                  type="button"
                  onClick={closeLightbox}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-200'
                      : 'hover:bg-fsgu-silver-100 text-fsgu-silver-700'
                  }`}
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Image */}
            <div className={`p-4 ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-white'}`}>
              <img
                src={lightboxSrc}
                alt={lightboxAlt}
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
              />
            </div>
          </div>
        </div>
      )}

      {/* Article */}
      <article className="section pt-32 pb-12">
        <div className="container-custom max-w-4xl">
          <Link
            to="/news"
            className={`inline-flex items-center mb-8 transition-colors ${
              isDarkMode
                ? 'text-fsgu-silver-400 hover:text-fsgu-silver-300'
                : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to News
          </Link>

          {post.category_name && (
            <div className="mb-4">
              <span
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: post.category_color || '#4CAF50' }}
              >
                <Tag className="w-4 h-4 mr-2" />
                {post.category_name}
              </span>
            </div>
          )}

          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${
            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
          }`}>
            {post.title}
          </h1>

          <div className={`flex flex-wrap items-center gap-6 mb-8 text-sm ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.published_at)}
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {post.views || 0} views
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-fsgu-green-500 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          {post.featured_image && (
            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto cursor-zoom-in"
                onClick={() => openLightbox(post.featured_image, post.title)}
              />
            </div>
          )}

          <div
            id="news-post-content"
            className={`prose prose-lg max-w-none mb-12 ${isDarkMode ? 'prose-invert' : ''}`}
            style={{ color: isDarkMode ? '#e2e8f0' : '#334155' }}
          >
            <style>{`
              .prose h2 {
                font-size: 1.875rem;
                font-weight: 700;
                margin-top: 2rem;
                margin-bottom: 1rem;
                color: ${isDarkMode ? '#f1f5f9' : '#1e293b'};
              }
              .prose h3 {
                font-size: 1.5rem;
                font-weight: 600;
                margin-top: 1.5rem;
                margin-bottom: 0.75rem;
                color: ${isDarkMode ? '#f1f5f9' : '#1e293b'};
              }
              .prose p {
                margin-bottom: 1.25rem;
                line-height: 1.75;
                font-size: 1.125rem;
              }
              .prose ul, .prose ol {
                margin: 1.25rem 0;
                padding-left: 1.75rem;
              }
              .prose li {
                margin-bottom: 0.75rem;
                line-height: 1.75;
              }
              .prose strong {
                font-weight: 600;
                color: ${isDarkMode ? '#f1f5f9' : '#0f172a'};
              }
              .prose a {
                color: #4CAF50;
                text-decoration: underline;
              }
              .prose a:hover {
                color: #66BB6A;
              }
              .prose img {
                display: block;
                max-width: 100%;
                height: auto;
                border-radius: 0.75rem;
                margin: 2rem auto;
              }
              @media (max-width: 768px) {
                .prose {
                  font-size: 1rem;
                }
                .prose h2 {
                  font-size: 1.5rem;
                  margin-top: 1.5rem;
                }
                .prose h3 {
                  font-size: 1.25rem;
                  margin-top: 1.25rem;
                }
                .prose p {
                  font-size: 1rem;
                  margin-bottom: 1rem;
                }
              }
            `}</style>

            <div dangerouslySetInnerHTML={{ __html: safeContentHtml }} />
          </div>
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section className={`section py-16 border-t ${
          isDarkMode ? 'border-fsgu-gray-800' : 'border-fsgu-silver-200'
        }`}>
          <div className="container-custom max-w-4xl">
            <h2 className={`text-3xl font-bold mb-8 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Related Articles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/news/${relatedPost.slug}`}
                  className={`block rounded-xl overflow-hidden transition-all hover:shadow-xl ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 hover:bg-fsgu-gray-750'
                      : 'bg-white hover:shadow-2xl'
                  }`}
                >
                  {relatedPost.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.featured_image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className={`font-bold mb-2 line-clamp-2 ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      {relatedPost.title}
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
                    }`}>
                      {formatDate(relatedPost.published_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsDetailPage;
